import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import he from "he";
import { Row, Spacer, TwoColGrid } from "../../../components/Layout/Layout";
import useTranslations from "../../../core/i18n/useTranslations";
import useUser from "../../../core/user/useUser";
import Button, {
  ButtonVariant,
} from "../../../ui-lib/components/Button/Button";
import { IOrganizationDetails } from "../../../core/api/organizations/types";
import {
  createOrganization,
  getOrganizationNames,
} from "../../../core/api/organizations/organizations";
import TextInput from "../../../ui-lib/components/Inputs/TextInput";
import Divider from "../../../ui-lib/components/Divider/Divider";
import { notifyApiErrors } from "../../../core/helpers/helpers";
import RadioGroup from "../../../ui-lib/components/Radio/RadioGroup";
import {
  validateEmailIfExists,
  validateEmergencyPhoneNumberIfExists,
  validatePhoneNumberIfExists,
} from "../../../core/helpers/validation";
import TextArea from "../../../ui-lib/components/Inputs/Textarea";
import usePageState from "../../../core/pagestate/usePageState";
import Dropdown from "../../../ui-lib/components/Dropdown/Dropdown";
import { IItemProp } from "../../../ui-lib/components/Dropdown/DropdownItem";
import CollapsibleBlock from "../../../ui-lib/components/CollapsibleBlock/CollapsibleBlock";
import { CustomDropdown } from "../../../ui-lib/components/Hierarchy/CustomTreeDropdown/CustomDropdown";
import PrimeModal from "../../../ui-lib/components/PrimeModal/PrimeModal";
import { AddressRow } from "../AddressRow";
import { TelephonesRow } from "../TelephonesRow";
import { BlockUserCostCenterRow } from "../BlockUserCostCenterRow";
import { SolutionTypeDropdown } from "../SolutionTypeDropdown";

export const AddOrganization = ({
  onSubmit,
  onClose,
  variant,
  sub = false,
  parentId,
  sellers,
}: {
  onSubmit?: (organizationId: number) => void;
  onClose?: () => void;
  variant: ButtonVariant;
  sub?: boolean;
  parentId?: number;
  sellers?: IItemProp[];
}) => {
  const t = useTranslations();
  const { authenticatedRequest, config } = useUser();
  const pageState = usePageState();
  const user = useUser();
  const [isAddOrganizationOpen, setIsAddOrganizationOpen] =
    useState<boolean>(false);
  const [hasParent, setHasParent] = useState<number>(0);

  const superAdmin = config?.show.includes("SkygdSuperAdmin");

  const {
    control,
    formState: { isSubmitting, errors },
    handleSubmit,
    reset,
    setValue,
    trigger,
  } = useForm<IOrganizationDetails>({
    defaultValues: {
      solutionType: 1,
    },
  });

  const radioItems = [
    { id: 0, value: "0", text: t("Organizations:first_level_organisation") },
    {
      id: 1,
      value: "1",
      text: t("Organizations:with_parent_level"),
      description: t("Organizations:below_parent_organisation"),
    },
  ];

  const closeModal = () => {
    setHasParent(0);
    reset();
    setIsAddOrganizationOpen(false);
    if (onClose !== undefined) {
      onClose();
    }
  };

  const onCreateOrganizationSubmit = async (formData: IOrganizationDetails) => {
    try {
      const responseData = await createOrganization(
        formData,
        authenticatedRequest,
      );
      onSubmit!(responseData.data);
      if (parentId) {
        const nameResponse = await getOrganizationNames(
          parentId.toString(),
          authenticatedRequest,
        );
        pageState.setInitialIds({
          name: nameResponse.data[0],
          id: +parentId,
        });
      }
      closeModal();
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    }
  };

  useEffect(() => {
    if (sub && parentId) {
      setValue("parentOrganizationId", +parentId);
      trigger("parentOrganizationId");
    }
  }, [isAddOrganizationOpen]);

  return (
    <>
      <Button
        variant={variant}
        image={sub ? "office-building" : undefined}
        text={t(
          sub
            ? "Organizations:add_sub_organization"
            : "Organizations:add_organization",
        )}
        onClick={() => {
          setIsAddOrganizationOpen(true);
        }}
        dataCy="add_organisation"
      />
      <PrimeModal
        withFooter
        withHeader
        fixedWidth="1000px"
        onClose={closeModal}
        isOpen={isAddOrganizationOpen}
        header={
          sub
            ? t("Organizations:create_new_sub_organization")
            : t("Organizations:create_new_organization")
        }
        submitBtn={{
          text: t("Organizations:create_organization"),
          onClick: handleSubmit(onCreateOrganizationSubmit),
        }}
        cancelBtn={{ text: t("Common:cancel"), onClick: closeModal }}
        loading={isSubmitting}
      >
        <p>{t("Organizations:create_organization_description")}</p>
        <Spacer size={16} />
        <form>
          {user.hasAccess("Sellers") && !sub && (
            <>
              <Row type="fill" align="fill">
                <RadioGroup
                  name="creation-type"
                  items={radioItems}
                  selectedValue={
                    radioItems.find((item) => item.id === hasParent)?.value
                  }
                  onChange={(value) => {
                    setHasParent(Number(value));
                  }}
                  label={t("Organizations:organization_level")}
                  labelHidden
                  fullWidth
                />
              </Row>
              <Spacer size={16} />
            </>
          )}
          {(!user.hasAccess("Sellers") || hasParent === 1 || sub) && (
            <>
              <Controller
                name="parentOrganizationId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <CustomDropdown
                    required
                    fullWidth
                    label={t("Organizations:parent_organization")}
                    selectionMode="single"
                    parentId={parentId?.toString()}
                    placeholder={t("Organizations:select_parent_organization")}
                    customerFilter={{
                      customers:
                        sub && parentId ? parentId.toString() : undefined,
                    }}
                    onSelectSignle={(selected) => {
                      setValue(field.name, selected?.key as number);
                      trigger("parentOrganizationId");
                    }}
                    validationError={
                      errors?.parentOrganizationId &&
                      t("Errors:input_field_required")
                    }
                  />
                )}
              />
              <Spacer size={16} />
            </>
          )}
          <TwoColGrid style={{ rowGap: 16 }}>
            <Controller
              name="name"
              control={control}
              rules={{
                required: true,
                minLength: 1,
                maxLength: 60,
              }}
              render={({ field }) => (
                <TextInput
                  required
                  inputRef={field.ref}
                  label={t("Common:name_label")}
                  placeholder={t("Organizations:org_col_name")}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={
                    errors?.name && t("Errors:input_organization_name")
                  }
                />
              )}
            />
            {user.hasAccess("OrganisationSolutionType") && (
              <Controller
                name="solutionType"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <SolutionTypeDropdown
                    field={field}
                    hasError={!!errors.solutionType}
                  />
                )}
              />
            )}
            <Controller
              name="organizationNumber"
              control={control}
              render={({ field }) => (
                <TextInput
                  placeholder={t("Common:digit_number")}
                  label={t("Organizations:organization_number")}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="customerNumber"
              control={control}
              rules={{
                validate: (value) => {
                  if (value && value.length <= 5) {
                    return validateEmergencyPhoneNumberIfExists(value);
                  }
                  return validatePhoneNumberIfExists(value);
                },
              }}
              render={({ field }) => (
                <TextInput
                  inputRef={field.ref}
                  placeholder="+46123456789"
                  label={t("Common:labels_phone_number")}
                  description={
                    errors.customerNumber
                      ? ""
                      : t("Common:telephone_placeholder")
                  }
                  value={field.value}
                  onChange={field.onChange}
                  validationError={
                    errors?.customerNumber &&
                    t("Errors:ValidationHint_TelephoneNumber")
                  }
                />
              )}
            />
            {superAdmin && (
              <>
                <Spacer size={32} />
                <Controller
                  name="sellerId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Dropdown
                      title={t("Common:belongs_to")}
                      placeholder={t("Administrator:seller_placeholder")}
                      items={sellers || []}
                      disabled={!sellers}
                      selectedItem={sellers?.find(
                        (seller) => seller.id === field.value,
                      )}
                      onSelectItem={(value?: IItemProp) => {
                        if (!value?.id) return;
                        setValue(field.name, value?.id as number);
                        trigger(field.name);
                      }}
                      validationError={
                        errors?.sellerId && t("Errors:input_field_required")
                      }
                    />
                  )}
                />
              </>
            )}
          </TwoColGrid>

          <AddressRow control={control} />

          <Spacer size={16} />

          <Row>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextInput
                  placeholder={t("Common:enter_city")}
                  label={t("Common:labels_city")}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Spacer size={32} />
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <TextInput
                  placeholder={t("Common:enter_country")}
                  label={t("Common:labels_country")}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Row>

          <Spacer size={32} />
          <Row>
            <Controller
              name="telefax"
              control={control}
              rules={{
                validate: (input: string | undefined) =>
                  !input || input.length < 2048,
              }}
              render={({ field }) => (
                <TextArea
                  maxSymbols={2048}
                  inputRef={field.ref}
                  label={t("Organizations:organization_info")}
                  placeholder={t("Organizations:organization_info_placeholder")}
                  value={he.decode(field.value ?? "")}
                  onChange={field.onChange}
                  validationError={
                    errors?.telefax &&
                    t("Errors:ValidationHint_OrganizationInfo")
                  }
                />
              )}
            />
          </Row>

          <Spacer size={32} />

          <Divider />

          <Spacer size={32} />

          <h2>{t("Organizations:contact_person_detail")}</h2>

          <Spacer size={16} />

          <Row>
            <Controller
              name="contactPerson"
              control={control}
              render={({ field }) => (
                <TextInput
                  placeholder={t("Common:contact_person_name")}
                  label={t("Common:name_label")}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Spacer size={32} />
            <Controller
              name="emailAddress"
              control={control}
              rules={{
                maxLength: 60,
                validate: validateEmailIfExists,
              }}
              render={({ field }) => (
                <TextInput
                  inputRef={field.ref}
                  placeholder={t("Common:placeholder_email")}
                  label={t("Common:email")}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={
                    errors?.emailAddress && t("Errors:input_email_format")
                  }
                />
              )}
            />
          </Row>

          <Spacer size={16} />

          <TwoColGrid>
            <Controller
              name="contactPersonAccessCode"
              control={control}
              render={({ field }) => (
                <TextInput
                  label={t("Organizations:contact_person_access_code")}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </TwoColGrid>

          <Spacer size={16} />

          <TelephonesRow control={control} errors={errors} />

          <Spacer size={32} />

          <Divider />

          <Spacer size={32} />

          <CollapsibleBlock withScroll title={t("Common:advanced_options")}>
            <>
              <Spacer size={16} />
              <BlockUserCostCenterRow control={control} />
            </>
          </CollapsibleBlock>
        </form>
      </PrimeModal>
    </>
  );
};
